/**
 * FlexEngine — Dexie.js Database Definition
 *
 * Schema version history:
 *   v1 — initial schema (Phase 2)
 *   v2 — added personalRecords table (Phase 5A)
 *
 * All UI/business logic must go through IFlexRepository, never Dexie directly.
 * This file is the only place Dexie is instantiated.
 */

import Dexie, { type Table } from 'dexie';
import type {
  Exercise,
  WorkoutSession,
  LoggedSet,
  MuscleState,
  GymProfile,
  AppSettings,
  PersonalRecord,
  WorkoutTemplate,
  BodyweightEntry,
} from '@/types';

export class FlexEngineDB extends Dexie {
  exercises!:        Table<Exercise,        string>;
  workouts!:         Table<WorkoutSession,  string>;
  loggedSets!:       Table<LoggedSet,       string>;
  muscleStates!:     Table<MuscleState,     string>;
  gymProfiles!:      Table<GymProfile,      string>;
  settings!:         Table<AppSettings,     string>;
  personalRecords!:  Table<PersonalRecord,  string>;
  workoutTemplates!: Table<WorkoutTemplate, string>;
  bodyweightLog!:    Table<BodyweightEntry, string>;

  constructor() {
    super('FlexEngineDB');

    this.version(1).stores({
      exercises:    'id, name, category, splitDay, pattern, isBuiltIn',
      workouts:     'id, startedAt, completedAt, splitDay, status, gymProfileId',
      loggedSets:   'id, workoutId, exerciseId, loggedAt, isWarmup, rpe',
      muscleStates: 'id, muscle, isInjured, lastUpdatedAt',
      gymProfiles:  'id, name, isActive, createdAt',
      settings:     'id',
    });

    // v2: personal records
    this.version(2).stores({
      exercises:       'id, name, category, splitDay, pattern, isBuiltIn',
      workouts:        'id, startedAt, completedAt, splitDay, status, gymProfileId',
      loggedSets:      'id, workoutId, exerciseId, loggedAt, isWarmup, rpe',
      muscleStates:    'id, muscle, isInjured, lastUpdatedAt',
      gymProfiles:     'id, name, isActive, createdAt',
      settings:        'id',
      personalRecords: 'id, exerciseId, achievedAt, workoutId',
    });

    // v3: workout templates
    this.version(3).stores({
      exercises:        'id, name, category, splitDay, pattern, isBuiltIn',
      workouts:         'id, startedAt, completedAt, splitDay, status, gymProfileId',
      loggedSets:       'id, workoutId, exerciseId, loggedAt, isWarmup, rpe',
      muscleStates:     'id, muscle, isInjured, lastUpdatedAt',
      gymProfiles:      'id, name, isActive, createdAt',
      settings:         'id',
      personalRecords:  'id, exerciseId, achievedAt, workoutId',
      workoutTemplates: 'id, name, splitDay, createdAt',
    });

    // v5: split type system — backfill splitType for existing users
    this.version(5).stores({
      exercises:        'id, name, category, splitDay, pattern, isBuiltIn',
      workouts:         'id, startedAt, completedAt, splitDay, status, gymProfileId',
      loggedSets:       'id, workoutId, exerciseId, loggedAt, isWarmup, rpe',
      muscleStates:     'id, muscle, isInjured, lastUpdatedAt',
      gymProfiles:      'id, name, isActive, createdAt',
      settings:         'id',
      personalRecords:  'id, exerciseId, achievedAt, workoutId',
      workoutTemplates: 'id, name, splitDay, createdAt',
      bodyweightLog:    'id, loggedAt',
    }).upgrade(async (tx) => {
      const s = await tx.table('settings').get('singleton');
      if (s) {
        if (!s.splitType) s.splitType = 'push_pull';
        // Normalise legacy split day values to new canonical names
        if (s.nextSplitDay === 'anterior') s.nextSplitDay = 'push';
        if (s.nextSplitDay === 'posterior') s.nextSplitDay = 'pull';
        await tx.table('settings').put(s);
      }
    });

    // v6: fix quad exercises — move splitDay from 'anterior' to 'legs'
    this.version(6).stores({
      exercises:        'id, name, category, splitDay, pattern, isBuiltIn',
      workouts:         'id, startedAt, completedAt, splitDay, status, gymProfileId',
      loggedSets:       'id, workoutId, exerciseId, loggedAt, isWarmup, rpe',
      muscleStates:     'id, muscle, isInjured, lastUpdatedAt',
      gymProfiles:      'id, name, isActive, createdAt',
      settings:         'id',
      personalRecords:  'id, exerciseId, achievedAt, workoutId',
      workoutTemplates: 'id, name, splitDay, createdAt',
      bodyweightLog:    'id, loggedAt',
    }).upgrade(async (tx) => {
      const quadExerciseIds = [
        'ex_squat_barbell', 'ex_leg_press', 'ex_leg_extension', 'ex_lunge_db',
        'ex_hack_squat', 'ex_front_squat', 'ex_goblet_squat', 'ex_bulgarian_split_squat',
        'ex_reverse_lunge_db', 'ex_step_up_db', 'ex_sumo_squat_db', 'ex_sissy_squat',
        'ex_smith_squat', 'ex_kb_goblet_squat',
      ];
      const exercises = await tx.table('exercises').bulkGet(quadExerciseIds);
      const toUpdate = exercises
        .filter((e: Exercise | undefined): e is Exercise => !!e && e.splitDay === 'anterior')
        .map((e: Exercise) => ({ ...e, splitDay: 'legs' as const }));
      if (toUpdate.length > 0) {
        await tx.table('exercises').bulkPut(toUpdate);
      }
    });

    // v4: bodyweight log + backfill new settings fields
    this.version(4).stores({
      exercises:        'id, name, category, splitDay, pattern, isBuiltIn',
      workouts:         'id, startedAt, completedAt, splitDay, status, gymProfileId',
      loggedSets:       'id, workoutId, exerciseId, loggedAt, isWarmup, rpe',
      muscleStates:     'id, muscle, isInjured, lastUpdatedAt',
      gymProfiles:      'id, name, isActive, createdAt',
      settings:         'id',
      personalRecords:  'id, exerciseId, achievedAt, workoutId',
      workoutTemplates: 'id, name, splitDay, createdAt',
      bodyweightLog:    'id, loggedAt',
    }).upgrade(async (tx) => {
      // Backfill new settings fields for existing users
      const s = await tx.table('settings').get('singleton');
      if (s) {
        if (!s.theme)             s.theme = 'dark';
        if (s.notificationsEnabled === undefined) s.notificationsEnabled = false;
        if (!s.notificationTime)  s.notificationTime = '08:00';
        await tx.table('settings').put(s);
      }
    });
  }
}

// Singleton instance — import this wherever DB access is needed
export const db = new FlexEngineDB();
